import React, { useState, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import { DropdownList } from 'react-widgets';
import { v4 as uuidv4 } from 'uuid';
import { i18n } from 'app/utils/i18n';
import { getSelectedCells, isStylingSelected, getSelectedDropdownValue } from './utils';
import { useReferenceData, useUpdateReferenceData, useUpdateReferenceTableCssClass } from './reference-data-queries';
import { useGetDocument } from 'app/api/documents/document-queries';
import { UserContext } from 'app/state/contexts/UserContext';
import { Spinner } from 'app/components';
import { StyledDataSheet } from 'app/components';
import { toolbarCSSExtractor, customClassExtractor, parseCellFormat, resolveCellStyling } from 'app/utils/css';
import { usePermissions } from 'app/utils/hooks/document-permission';

export function DetailsTable() {
  const { documentId, refId } = useParams();
  const { selectedOrganization } = useContext(UserContext);
  const { data: document } = useGetDocument(documentId, selectedOrganization);
  const docCss = document?.css;
  const exposedCss = document?.exposedCss;
  const locale = document?.content.referenceData.find((rd) => rd.superId === refId)?.locale ?? document?.content.locale;
  const showOriginalValue =
    (document?.content.referenceData.find((rd) => rd.superId === refId)?.locale ?? document?.content.locale) === (document?.content.locale ?? 'sv-SE');

  const { documentPermissions } = usePermissions(documentId);
  const { mutate: updateReferenceData } = useUpdateReferenceData();
  const referenceData = useReferenceData(refId, documentId);
  const { data } = referenceData;

  const normalCellLabel = i18n('reference-data.data-table.normal-cell');
  const tableHeadLabel = i18n('reference-data.data-table.table-head');

  const elementOptions = [
    { reset: true, label: normalCellLabel },
    { element: 'th', label: tableHeadLabel },
  ];

  const builtinCellClasses = [
    { className: 'table--heading', element: null, label: i18n('reference-data.data-table.table-title') },
    { className: 'table--part-sum', element: null, label: i18n('reference-data.data-table.part-sum') },
    { className: 'table--sum', element: null, label: i18n('reference-data.data-table.sum') },
  ];

  const borderFormats = [
    { id: 1, value: 'clear', label: i18n('reference-data.data-table.no-border') },
    { id: 2, value: 'table--border-top', label: i18n('reference-data.data-table.border-top') },
    { id: 3, value: 'table--border-bottom', label: i18n('reference-data.data-table.border-bottom') },
    { id: 4, value: 'table--border-left', label: i18n('reference-data.data-table.border-left') },
    { id: 5, value: 'table--border-right', label: i18n('reference-data.data-table.border-right') },
    { id: 6, value: 'table--border-top-bottom', label: i18n('reference-data.data-table.border-top-bottom') },
    { id: 7, value: 'table--border-left-right', label: i18n('reference-data.data-table.border-left-right') },
    { id: 8, value: 'table--border-all', label: i18n('reference-data.data-table.border-all') },
  ];

  const backgrounds = [
    { id: 1, value: 'clear', label: i18n('reference-data.data-table.no-background') },
    { id: 2, value: 'table--background-primary', label: i18n('reference-data.data-table.primary-background') },
    { id: 3, value: 'table--background-secondary', label: i18n('reference-data.data-table.secondary-background') },
    { id: 4, value: 'table--background-tertiary', label: i18n('reference-data.data-table.tertiary-background') },
    { id: 5, value: 'table--background-quaternary', label: i18n('reference-data.data-table.quaternary-background') },
  ];

  const { user } = useContext(UserContext);
  const lang = user?.userProperties?.cultureId ?? 'sv';

  const { classes: availableTableClassObjects, error: tableClassParseError } = customClassExtractor(docCss, 'table');
  const availableTableClasses = availableTableClassObjects.map((c) => c.className);
  const tableClassLabelMap = React.useMemo(() => {
    const map = {};
    for (const c of availableTableClassObjects) {
      map[c.className] = lang === 'en' ? c.labelEn : c.labelSv;
    }
    return map;
  }, [availableTableClassObjects, lang]);
  const { mutate: updateCssClass } = useUpdateReferenceTableCssClass();

  const tableCssClasses = React.useMemo(() => {
    const cssClass = document?.content?.referenceData?.find((rd) => rd.superId === refId)?.cssClass || '';
    return cssClass
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
  }, [document, refId]);

  const handleAddTableClass = (cls) => {
    if (!tableCssClasses.includes(cls)) {
      const updated = [...tableCssClasses, cls];
      updateCssClass({ documentSuperId: documentId, referenceDataSuperId: refId, cssClass: updated.join(', ') });
    }
  };

  const handleRemoveTableClass = (cls) => {
    const updated = tableCssClasses.filter((c) => c !== cls);
    updateCssClass({ documentSuperId: documentId, referenceDataSuperId: refId, cssClass: updated.join(', ') });
  };

  const unselectedTableClasses = availableTableClasses.filter((cls) => !tableCssClasses.includes(cls));

  const [selection, setSelection] = useState();
  const [, setCellStylingRevision] = useState(0);
  const [cellStylingOpen, setCellStylingOpen] = useState(false);
  const keepCellStylingOpen = useRef(false);

  const getDropdownSelector = (type, options) => {
    return (data, selection) => getSelectedDropdownValue(type, data, selection, options);
  };

  const financialToolbars = toolbarCSSExtractor(docCss, lang, 'financial');

  // Financial-toolbar entries are selectors: ".sum" (class only), "td.td-75px"
  // (element + class) or "th" (element only). All three are offered; the element
  // half, when present, moves the cell and the class half is appended.
  const cellClassOptions = React.useMemo(() => {
    const options = [...builtinCellClasses];
    const seen = new Set(options.map((o) => o.className));
    for (const option of financialToolbars[0]?.options ?? []) {
      const { element, className } = parseCellFormat(option.selector);
      if ((!className && !element) || seen.has(className || element)) continue;
      seen.add(className || element);
      options.push({ className, element, label: option.title });
    }
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docCss, lang]);

  const cellClassLabel = (className) => cellClassOptions.find((o) => o.className === className)?.label ?? className;

  // One dropdown drives both slots: the two element entries behave like radios,
  // the class entries toggle independently.
  const cellStylingOptions = [...elementOptions, ...cellClassOptions];

  const getSelectedBorder = getDropdownSelector('border', borderFormats);
  const getSelectedBackground = getDropdownSelector('background', backgrounds);

  const handleClick = (styling) => {
    if (!selection) return;

    getSelectedCells(selection).forEach(({ x, y }) => {
      if (data.selectionContent[x][y]['styling'] === styling) {
        delete data.selectionContent[x][y]['styling'];
      } else {
        data.selectionContent[x][y]['styling'] = styling;
      }
    });

    setSelection(null);
  };

  const setStyle = (key, value) => {
    if (!selection) return;

    getSelectedCells(selection).forEach(({ x, y }) => {
      data.selectionContent[x][y][key] = value;
    });

    setSelection(null);
  };

  const clearStyle = (key) => {
    if (!selection) return;

    getSelectedCells(selection).forEach(({ x, y }) => {
      data.selectionContent[x][y][key] = '';
      delete data.selectionContent[x][y][key];
    });

    setSelection(null);
  };

  // Writes `classes` + an element-only `format` back onto a cell, folding any
  // classes a legacy row still carries inside `format` into `classes`.
  const writeCellStyling = (cell, { classes, isHead }) => {
    if (classes.length > 0) {
      cell.classes = classes;
    } else {
      delete cell.classes;
    }
    if (isHead) {
      cell.format = 'thead';
    } else {
      delete cell.format;
    }
  };

  const updateSelectedCells = (update) => {
    if (!selection) return;

    getSelectedCells(selection).forEach(({ x, y }) => {
      const cell = data.selectionContent[x][y];
      writeCellStyling(cell, update(resolveCellStyling(cell)));
    });

    setCellStylingRevision((n) => n + 1);
  };

  const selectedCells = getSelectedCells(selection).map(({ x, y }) => resolveCellStyling(data?.selectionContent?.[x]?.[y]));

  // Classes on every selected cell, and the shared element (null when mixed).
  const selectedCellClasses = selectedCells.length
    ? selectedCells[0].classes.filter((className) => selectedCells.every((cell) => cell.classes.includes(className)))
    : [];
  const selectedIsHead = !selectedCells.some((cell) => cell.isHead) ? false : selectedCells.every((cell) => cell.isHead) ? true : null; // mixed selection — neither element entry is ticked

  const isOptionSelected = (option) => {
    if (selectedCells.length === 0) return false;
    if (option.reset) return selectedIsHead === false && selectedCellClasses.length === 0;
    if (option.className) return selectedCellClasses.includes(option.className);
    return selectedIsHead === true;
  };

  const handleToggleOption = (option) => {
    if (option.reset) {
      updateSelectedCells(() => ({ classes: [], isHead: false }));
      return;
    }

    if (!option.className) {
      const nextIsHead = selectedIsHead !== true;
      updateSelectedCells(({ classes }) => ({ classes, isHead: nextIsHead }));
      return;
    }

    if (selectedCellClasses.includes(option.className)) {
      updateSelectedCells(({ classes, isHead }) => ({ classes: classes.filter((c) => c !== option.className), isHead }));
      return;
    }

    updateSelectedCells(({ classes, isHead }) => ({
      classes: [...classes, option.className],
      // An element-qualified selector (`th`, `th.x`) only matches when the cell is
      // that element, so applying it has to move the cell too. A class-only
      // selector leaves the element alone.
      isHead: option.element ? option.element === 'th' : isHead,
    }));
  };

  const selectedSummary = [...(selectedIsHead ? [tableHeadLabel] : []), ...selectedCellClasses.map(cellClassLabel)];
  const cellStylingValue = { label: selectedSummary.length > 0 ? selectedSummary.join(', ') : normalCellLabel };

  const handleBackgroundChange = (menuItem) => {
    if (menuItem.value === 'clear') {
      clearStyle('background');
    } else {
      setStyle('background', menuItem.value);
    }
  };

  const handleBorderChange = (menuItem) => {
    if (menuItem.value === 'clear') {
      clearStyle('border');
    } else {
      setStyle('border', menuItem.value);
    }
  };

  const handleSubmit = () => {
    const referenceData = {
      name: data.name,
      documentSuperId: documentId,
      fileRevisionId: data.fileRevisionId,
      fileSuperId: data.fileSuperId,
      selection: data.selection,
      selectionContent: data.selectionContent,
      superId: data.superId,
      revisionId: uuidv4(),
    };

    updateReferenceData({ document, referenceData });
  };

  if (!referenceData.isSuccess) {
    return <Spinner />;
  }

  return (
    <div>
      {(documentPermissions.includes('CODER') || documentPermissions.includes('DESIGN')) && (availableTableClasses.length > 0 || tableClassParseError) && (
        <div className="d-flex align-items-center flex-wrap gap-2 px-2 py-2">
          {tableClassParseError && (
            <Tippy content={<p className="gaby-tooltip arrow-top">{tableClassParseError}</p>} placement="bottom" theme="light">
              <span className="text-warning">
                <i className="fa-regular fa-triangle-exclamation" />
              </span>
            </Tippy>
          )}
          {availableTableClasses.length > 0 && (
            <>
              <div className="rw-dropdown-wrapper" style={{ minWidth: '200px' }}>
                <DropdownList
                  data={unselectedTableClasses.map((cls) => ({ id: cls, value: cls, label: tableClassLabelMap[cls] || cls }))}
                  textField="label"
                  placeholder={i18n('reference-data.data-table.select-css-classes')}
                  onSelect={(item) => handleAddTableClass(item.value)}
                  value={null}
                  filter="contains"
                  selectIcon={<i className="fa-regular fa-chevron-down" />}
                  onChange={() => {}}
                />
              </div>
              {tableCssClasses.map((cls) => (
                <span key={cls} className="badge bg-blue-100 text-blue-700 d-inline-flex align-items-center px-2 py-1 rounded-pill">
                  {cls}
                  <button
                    type="button"
                    className="bg-transparent border-0 p-0 ms-1"
                    onClick={() => handleRemoveTableClass(cls)}
                    style={{ fontSize: '0.6rem', lineHeight: 1 }}
                  >
                    <i className="fa-regular fa-xmark" />
                  </button>
                </span>
              ))}
            </>
          )}
        </div>
      )}
      <div className="reference-data--toolbar">
        <div className="rw-dropdown-wrapper" style={{ display: 'flex', flexGrow: 1 }}>
          <DropdownList
            data={cellStylingOptions}
            selectIcon={<i className="fa-regular fa-chevron-down" />}
            textField="label"
            value={cellStylingValue}
            open={cellStylingOpen}
            onToggle={(open) => {
              if (!open && keepCellStylingOpen.current) {
                keepCellStylingOpen.current = false;
                return;
              }
              setCellStylingOpen(open);
            }}
            onSelect={(option) => {
              keepCellStylingOpen.current = true;
              handleToggleOption(option);
            }}
            renderListItem={({ item }) => (
              <span className="d-flex align-items-center gap-2">
                <i className={`fa-regular fa-check ${isOptionSelected(item) ? '' : 'invisible'}`} />
                {item.label}
              </span>
            )}
            onChange={() => {}}
          />
        </div>
        <div className="divider" />
        <div className="rw-dropdown-wrapper">
          <DropdownList
            data={borderFormats}
            selectIcon={<i className="fa-regular fa-chevron-down" />}
            textField="label"
            defaultValue={borderFormats[0]}
            value={getSelectedBorder(data, selection)}
            onSelect={handleBorderChange}
            onChange={() => {}}
          />
        </div>
        <div className="rw-dropdown-wrapper">
          <DropdownList
            data={backgrounds}
            selectIcon={<i className="fa-regular fa-chevron-down" />}
            textField="label"
            defaultValue={backgrounds[0]}
            value={getSelectedBackground(data, selection)}
            onSelect={handleBackgroundChange}
            onChange={() => {}}
          />
        </div>
        <div className="divider" />
        <div className="py-1 d-flex align-items-center">
          <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('reference-table.bold')}</p>} placement="bottom" theme="light">
            <button
              onClick={() => {
                handleClick('bold');
              }}
              className={`${isStylingSelected('bold', data, selection) ? 'active' : ''}`}
            >
              <i className="fa-regular fa-bold" />
            </button>
          </Tippy>
          <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('reference-table.italic')}</p>} placement="bottom" theme="light">
            <button
              onClick={() => {
                handleClick('italic');
              }}
              className={`${isStylingSelected('italic', data, selection) ? 'active' : ''}`}
            >
              <i className="fa-regular fa-italic" />
            </button>
          </Tippy>
          <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('reference-table.underline')}</p>} placement="bottom" theme="light">
            <button
              onClick={() => {
                handleClick('underline');
              }}
              className={`${isStylingSelected('underline', data, selection) ? 'active' : ''}`}
            >
              <i className="fa-regular fa-underline" />
            </button>
          </Tippy>
        </div>
        <div className="divider" />
        <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('reference-table.link-note')}</p>} placement="bottom" theme="light">
          <button>{i18n('reference-table.link-note')}</button>
        </Tippy>
        <div className="divider" />
        <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('common.save')}</p>} placement="bottom" theme="light">
          <button onClick={handleSubmit}>{i18n('common.save')}</button>
        </Tippy>
      </div>
      <div className="rd-table-scope" style={{ overflow: 'auto' }}>
        <style>{exposedCss}</style>
        <style>{`@scope (.rd-table-scope) { ${docCss} .page-content { height: auto !important; overflow: visible !important; width: auto !important; display: block !important; background: none !important; } }`}</style>
        <div className="page-content">
          <StyledDataSheet
            data={data.selectionContent}
            onSelect={setSelection}
            locale={locale}
            showOriginalValue={showOriginalValue}
            cssClass={tableCssClasses.join(' ')}
          />
        </div>
      </div>
    </div>
  );
}
